import React, { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Sparkles, Quote } from "lucide-react";

/* ---------------- Examples data ---------------- */
const EXAMPLES_DATA = {
  nike: {
    slug: "nike",
    brand: "Nike",
    title: "How Nike Built a Brand People Believe In",
    heroImage:
      "https://customer-assets.emergentagent.com/job_tale-builder-110/artifacts/rcn2w344_ChatGPT%20Image%20Jun%2016%2C%202026%2C%2004_24_24%20PM.png",
    metaTitle:
      "Nike Brand Story Example | What Founders Can Learn | The Story Assembly",
    metaDescription:
      "Explore Nike's brand story, positioning, messaging and emotional strategy. Learn why Nike remains one of the world's most powerful brands.",
    intro: [
      "Nike doesn't sell shoes.",
      "It sells belief.",
      "At its core, Nike's story is about helping people push beyond limits and discover what they're capable of becoming.",
      "Whether you're an Olympic athlete or someone running their first kilometer, Nike's message remains consistent:",
    ],
    pullQuote: "Greatness is not reserved for the chosen few.",
    introOutro:
      "That story has allowed Nike to build emotional connections that go far beyond products.",
    backstory: {
      kicker: "Origin",
      heading: "The story behind Nike",
      paragraphs: [
        "Nike was founded in 1964 by Phil Knight and Bill Bowerman.",
        "What began as a company distributing running shoes eventually evolved into one of the most influential brands in the world.",
        "The turning point wasn't product innovation alone.",
        "It was storytelling.",
        "Nike stopped talking about footwear and started talking about ambition, resilience and achievement.",
      ],
    },
    positioning: {
      statement:
        "Nike empowers athletes and aspiring athletes to unlock their potential through performance, innovation and belief.",
      audience: [
        "Athletes",
        "Fitness enthusiasts",
        "Aspiring achievers",
        "Everyday consumers seeking self-improvement",
      ],
      promise: {
        intro: "Nike doesn't promise better shoes.",
        sub: "Nike promises:",
        line: "A better version of yourself.",
      },
    },
    whyItWorks: [
      {
        title: "It focuses on identity",
        body: [
          "People don't buy products.",
          "They buy identities.",
          "Nike customers aren't purchasing sneakers. They're buying a symbol of discipline, ambition and performance.",
        ],
      },
      {
        title: "It inspires action",
        body: [
          "Few slogans in history are as powerful as:",
          "Just Do It.",
          "Three words. A complete philosophy.",
        ],
      },
      {
        title: "It remains consistent",
        body: [
          "For decades, Nike has communicated the same emotional message:",
          "Believe in yourself. Push harder. Keep going.",
        ],
      },
    ],
    founderLessons: {
      intro: [
        "Most founders describe features.",
        "Nike describes transformation.",
        "Instead of saying:",
      ],
      contrast: {
        before: "We sell running shoes.",
        afterIntro: "Nike says:",
        after: "We help people achieve greatness.",
      },
      questions: [
        "What transformation do you create?",
        "How does your customer change?",
        "What belief does your brand represent?",
      ],
      outro:
        "Those answers are often more powerful than product features.",
    },
    ifWeWroteIt: {
      story:
        "Nike exists to remind people that greatness lives inside everyone. Whether you're competing on the world stage or taking your first step toward a goal, every achievement begins with belief. Nike creates products and experiences that help athletes of every level move further, train harder and become more than they were yesterday. Because greatness isn't born — it's built.",
      taglines: ["Just Do It.", "Built For Greatness.", "Believe In Your Potential."],
      tone: ["Confident", "Motivational", "Energetic", "Empowering"],
    },
  },
};

export const EXAMPLE_SLUGS = Object.keys(EXAMPLES_DATA);

const Kicker = ({ children }) => (
  <div className="font-mono-display text-[11px] uppercase tracking-[0.22em] text-[#6B655B]">
    {children}
  </div>
);

const H2 = ({ children }) => (
  <h2 className="font-serif text-4xl md:text-5xl tracking-tighter text-[#0A1E3F] leading-[1.05] mt-3">
    {children}
  </h2>
);

const ExamplePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const data = EXAMPLES_DATA[slug];

  useEffect(() => {
    if (!data) return;
    const prevTitle = document.title;
    document.title = data.metaTitle;
    const meta =
      document.querySelector('meta[name="description"]') ||
      (() => {
        const m = document.createElement("meta");
        m.setAttribute("name", "description");
        document.head.appendChild(m);
        return m;
      })();
    const prevDesc = meta.getAttribute("content");
    meta.setAttribute("content", data.metaDescription);
    return () => {
      document.title = prevTitle;
      if (prevDesc !== null) meta.setAttribute("content", prevDesc);
    };
  }, [data]);

  if (!data) {
    return (
      <div className="min-h-screen bg-[#F4F1EA] flex items-center justify-center px-6">
        <div className="text-center">
          <Kicker>404</Kicker>
          <h1 className="font-serif text-5xl tracking-tighter mt-2 text-[#0A1E3F]">
            Example not found.
          </h1>
          <button
            onClick={() => navigate("/")}
            className="mt-6 inline-flex items-center gap-2 rounded-full px-5 py-3 bg-[#0A1E3F] text-[#F4F1EA] text-sm"
          >
            <ArrowLeft size={14} /> Back home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F1EA] text-[#1C1A17]">
      <header className="border-b border-[#DCD7C9]">
        <div className="max-w-3xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link
            to="/"
            data-testid="example-back"
            className="inline-flex items-center gap-2 text-sm text-[#0A1E3F] hover:text-[#D4A53A] transition-colors"
          >
            <ArrowLeft size={14} /> The Story Assembly
          </Link>
          <div className="flex items-center gap-2">
            <img
              src="https://customer-assets.emergentagent.com/job_tale-builder-110/artifacts/rxbavk25_ChatGPT%20Image%20May%2029%2C%202025%2C%2010_23_27%20PM.png"
              alt="The Story Assembly"
              className="h-7 w-7 rounded-md object-cover"
            />
            <span className="font-serif text-base tracking-tight hidden sm:inline">
              The <span className="italic font-light">Story</span>{" "}
              <span className="text-[#D4A53A]">Assembly</span>
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-10 py-14 md:py-20">
        <Kicker>Brand Story Example · {data.brand}</Kicker>
        <h1
          data-testid="example-title"
          className="font-serif text-5xl md:text-7xl tracking-tighter mt-3 leading-[1.02] text-[#0A1E3F]"
        >
          {data.title}
        </h1>

        {data.heroImage && (
          <figure className="mt-10 -mx-6 md:-mx-10">
            <img
              src={data.heroImage}
              alt={`${data.brand} brand story — hero illustration`}
              data-testid="example-hero-image"
              loading="eager"
              className="w-full h-auto object-cover md:rounded-md"
            />
          </figure>
        )}

        {/* Intro */}
        <section className="mt-12 space-y-5 text-[17px] leading-[1.7] text-[#1C1A17]/85">
          {data.intro.map((p) => (
            <p key={p}>{p}</p>
          ))}
        </section>

        <blockquote className="my-12 border-l-2 border-[#D4A53A] pl-5">
          <Quote size={18} className="text-[#D4A53A] mb-2" />
          <p className="font-serif text-3xl md:text-4xl leading-tight italic text-[#0A1E3F]">
            {data.pullQuote}
          </p>
        </blockquote>

        <p className="text-[17px] leading-[1.7] text-[#1C1A17]/85">
          {data.introOutro}
        </p>

        {/* Backstory */}
        <section className="mt-16">
          <Kicker>{data.backstory.kicker}</Kicker>
          <H2>{data.backstory.heading}</H2>
          <div className="mt-7 space-y-4 text-[17px] leading-[1.7] text-[#1C1A17]/85">
            {data.backstory.paragraphs.map((p) => (
              <p key={p}>{p}</p>
            ))}
          </div>
        </section>

        {/* Positioning */}
        <section className="mt-16">
          <Kicker>Positioning</Kicker>
          <H2>Nike's positioning</H2>
          <div className="mt-7 border border-[#DCD7C9] rounded-md p-6 bg-[#F4F1EA]">
            <Kicker>Positioning statement</Kicker>
            <p className="font-serif text-2xl md:text-3xl leading-snug text-[#0A1E3F] mt-2">
              {data.positioning.statement}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-3 mt-3">
            <div className="border border-[#DCD7C9] rounded-md p-6 bg-[#F4F1EA]">
              <Kicker>Core audience</Kicker>
              <ul className="mt-3 space-y-1.5 text-[15px] text-[#1C1A17]/85">
                {data.positioning.audience.map((a) => (
                  <li key={a} className="flex gap-2">
                    <span className="text-[#D4A53A]">·</span> {a}
                  </li>
                ))}
              </ul>
            </div>
            <div className="border border-[#0A1E3F]/20 rounded-md p-6 bg-[#0A1E3F] text-[#F4F1EA]">
              <Kicker>
                <span className="!text-[#D4A53A]">Emotional promise</span>
              </Kicker>
              <p className="text-[15px] mt-3 text-[#F4F1EA]/75">
                {data.positioning.promise.intro}
              </p>
              <p className="text-[13px] mt-3 text-[#F4F1EA]/55 uppercase tracking-[0.18em] font-mono-display">
                {data.positioning.promise.sub}
              </p>
              <p className="font-serif italic text-2xl mt-2 text-[#D4A53A]">
                {data.positioning.promise.line}
              </p>
            </div>
          </div>
        </section>

        {/* Why it works */}
        <section className="mt-16">
          <Kicker>What works</Kicker>
          <H2>Why Nike's story works</H2>
          <div className="mt-8 space-y-8">
            {data.whyItWorks.map((item, i) => (
              <div
                key={item.title}
                className="border-l-2 border-[#D4A53A]/50 pl-6"
              >
                <div className="font-mono-display text-[10px] uppercase tracking-[0.22em] text-[#D4A53A]">
                  0{i + 1}
                </div>
                <h3 className="font-serif text-2xl md:text-3xl text-[#0A1E3F] tracking-tight mt-1">
                  {item.title}
                </h3>
                <div className="mt-3 space-y-3 text-[16px] leading-[1.7] text-[#1C1A17]/85">
                  {item.body.map((p) => (
                    <p key={p}>{p}</p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Founder lessons */}
        <section className="mt-16">
          <Kicker>For founders</Kicker>
          <H2>What founders can learn from Nike</H2>
          <div className="mt-7 space-y-4 text-[17px] leading-[1.7] text-[#1C1A17]/85">
            {data.founderLessons.intro.map((p) => (
              <p key={p}>{p}</p>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-3 mt-5">
            <div className="border border-[#DCD7C9] rounded-md p-5 bg-[#F4F1EA]">
              <Kicker>Before</Kicker>
              <p className="font-serif text-xl mt-2 text-[#1C1A17]/60 italic">
                {data.founderLessons.contrast.before}
              </p>
            </div>
            <div className="border border-[#D4A53A] rounded-md p-5 bg-[#D4A53A]/10">
              <Kicker>
                <span className="!text-[#D4A53A]">
                  {data.founderLessons.contrast.afterIntro}
                </span>
              </Kicker>
              <p className="font-serif text-xl mt-2 text-[#0A1E3F]">
                {data.founderLessons.contrast.after}
              </p>
            </div>
          </div>
          <p className="mt-8 text-[15px] text-[#1C1A17]/70 uppercase tracking-[0.18em] font-mono-display">
            Ask yourself
          </p>
          <ul className="mt-3 space-y-2 text-[18px] font-serif italic text-[#0A1E3F]">
            {data.founderLessons.questions.map((q) => (
              <li key={q}>· {q}</li>
            ))}
          </ul>
          <p className="mt-7 text-[16px] text-[#1C1A17]/85 leading-relaxed">
            {data.founderLessons.outro}
          </p>
        </section>

        {/* If we wrote it */}
        <section className="mt-20 border border-[#0A1E3F]/15 rounded-md bg-[#0A1E3F] text-[#F4F1EA] p-6 md:p-10">
          <Kicker>
            <span className="!text-[#D4A53A]">
              If The Story Assembly wrote Nike's brand
            </span>
          </Kicker>
          <p className="mt-5 font-mono-display text-[10px] uppercase tracking-[0.22em] text-[#F4F1EA]/55">
            Brand story
          </p>
          <p className="font-serif text-[22px] md:text-[24px] leading-[1.5] mt-3 text-[#F4F1EA]">
            {data.ifWeWroteIt.story}
          </p>

          <p className="mt-8 font-mono-display text-[10px] uppercase tracking-[0.22em] text-[#F4F1EA]/55">
            Tagline directions
          </p>
          <ul className="mt-3 space-y-2">
            {data.ifWeWroteIt.taglines.map((t, i) => (
              <li
                key={t}
                className="font-serif text-2xl border-l-2 border-[#D4A53A]/60 pl-4"
              >
                <span className="font-mono-display text-[10px] text-[#F4F1EA]/45 mr-2">
                  0{i + 1}
                </span>
                {t}
              </li>
            ))}
          </ul>

          <p className="mt-8 font-mono-display text-[10px] uppercase tracking-[0.22em] text-[#F4F1EA]/55">
            Tone of voice
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {data.ifWeWroteIt.tone.map((w) => (
              <span
                key={w}
                className="rounded-full border border-[#D4A53A]/40 text-[#D4A53A] text-[12px] font-mono-display uppercase tracking-[0.18em] px-3 py-1"
              >
                {w}
              </span>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="mt-20 text-center border-t border-[#DCD7C9] pt-14">
          <Kicker>Your turn</Kicker>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tighter mt-3 text-[#0A1E3F] leading-tight">
            Create your own brand story.
          </h2>
          <p className="mt-5 text-[16px] text-[#1C1A17]/75 max-w-lg mx-auto">
            Nike's story wasn't built by accident. The strongest brands know
            exactly who they serve, what they stand for, and how they make people
            feel. Try The Story Assembly and generate yours in under 60 seconds.
          </p>
          <Link to="/#demo" className="inline-block mt-7">
            <button
              data-testid="example-cta"
              className="inline-flex items-center gap-2 rounded-full px-6 py-3 bg-[#D4A53A] text-[#0A1E3F] text-sm font-medium hover:bg-[#B58A26] transition-all active:scale-[0.97]"
            >
              <Sparkles size={15} /> Generate your brand story
              <ArrowRight size={14} />
            </button>
          </Link>
        </section>
      </main>
    </div>
  );
};

export default ExamplePage;
